import React, { useEffect, useState, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import { getAccountBalance } from '../../utilities/api'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import { Balance } from './Balance'
import { Payouts } from './Payouts'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { EmptyContainer } from './../EmptyContainer'
import { ViewPayoutWrapper } from './ViewPayoutWrapper'
import { ViewAllPayoutsWrapper } from './ViewAllPayoutsWrapper'

import './accountInformationWrapper.scss'

export default function AccountInformationWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_BALANCE_PAYOUTS
    )

  const navigate = useNavigate()
  const location = useLocation()

  const [account, setAccount] = useState()

  const [bankAccount, setBankAccount] = useState()

  const [balance, setBalance] = useState()

  const [accountId, setAccountId] = useState(null)

  const [payouts, setPayouts] = useState()

  // single payout
  const [payout, setPayout] = useState()

  const organization = AuthService.getOrg()?.[0]
  const activeOrgUuid = organization?.uuid

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    const org = AuthService.getOrg()?.[0]
    const nextId = org?.connected_account_id ?? null
    setAccountId(nextId)
    setPayout(undefined)
    if (!nextId) {
      setBankAccount(undefined)
      setBalance(undefined)
      setAccount(undefined)
      setPayouts(undefined)
    }
  }, [orgPermissionsReady, hasPermission, activeOrgUuid])

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (accountId) loadAccountBalance(accountId)
  }, [accountId, orgPermissionsReady, hasPermission])

  const loadAccountBalance = (accountId) => {
    showLoading()
    getAccountBalance(accountId)
      .then((res) => {
        setBankAccount(res.data?.linkedBankAccount)
        setBalance(res?.data?.balance)
        setAccount(res?.data?.account)
        setPayouts(res?.data?.payouts?.data)
        hideLoading()
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }

  const path = location.pathname
  // Matches any URL that ends with /payouts or /payouts/
  const isViewAllPayouts = /\/payouts\/?$/.test(path)

  // Matches URL that ends with /payouts/:id but NOT /payouts/ or /payouts/:id/anything
  const isViewSinglePayout = /\/payouts\/[^/]+$/.test(path)
  const isStandalonePayoutsReportRoute = path.startsWith('/payouts-report')
  const pageLoadingStyle = isStandalonePayoutsReportRoute
    ? 'without-sidebar'
    : ''
  const payoutIdFromPath = path.match(/\/payouts\/([^/]+)$/)?.[1] ?? null

  useEffect(() => {
    if (!isViewSinglePayout || !payoutIdFromPath || !payouts?.length) return

    const match = payouts.find((p) => p?.id === payoutIdFromPath)
    if (match && payout?.id !== match.id) {
      setPayout(match)
    }
  }, [isViewSinglePayout, payoutIdFromPath, payouts, payout?.id])

  // handle different views
  const handleView = (_, payout) => {
    if (payout) {
      let url
      setPayout(payout)

      // from payouts/
      if (isViewAllPayouts) url = `${payout.id}`
      // from account-information/
      else url = `payouts/${payout.id}`

      navigate(url)
    } else {
      setPayout()
      navigate('payouts')
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style={pageLoadingStyle} />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer style={pageLoadingStyle} />
  }

  return (
    <div className="position-relative" id="account-info">
      {!isViewAllPayouts && !isViewSinglePayout ? (
        <section className="wrapper" data-testid="account-information-section">
          <header className="section-header">
            <div className="section-heading section-heading--secondary">
              <h1>Payouts Report</h1>
            </div>
            <p className="section-header-desc">
              The money you make from your events will be issued to the bank
              account associated with this account
            </p>
          </header>
          {bankAccount && balance && bankAccount && payouts ? (
            <>
              <section>
                <Balance
                  account={account}
                  balance={balance}
                  bankAccount={bankAccount}
                  payouts={payouts}
                />
              </section>
              <section>
                <Payouts
                  payouts={payouts}
                  bankAccount={bankAccount}
                  handleView={handleView}
                />
              </section>
            </>
          ) : (
            <Card body className="card--md">
              <EmptyContainer style="center lg">
                <p>No account available</p>
                {/* <Button
                  size="lg"
                  className="w-75 btn-plus icon-button--white"
                >
                  Connect with Stripe
                </Button> */}
              </EmptyContainer>
            </Card>
          )}
        </section>
      ) : (
        <>
          {isViewSinglePayout ? (
            <ViewPayoutWrapper payout={payout} accountId={accountId} />
          ) : (
            <ViewAllPayoutsWrapper
              bankAccount={bankAccount}
              payouts={payouts}
              handleView={handleView}
            />
          )}
        </>
      )}
    </div>
  )
}
